/**
 * The app's translation function, shaped like react-i18next's hook so
 * components read naturally, over the app's own small i18n (i18nSetup).
 *
 * Reactive to the language (B72): it subscribes to the app's language
 * (subscribeToLanguage), so every screen that uses it redraws in the new
 * language the moment it changes. That matters on a cold load of any page: the
 * app draws first in the language the device starts in, and the signed-in
 * account's own language arrives a moment later; before this, a page that read
 * nothing else (the Subscribe page typed into the address bar) stayed in the
 * first language.
 *
 * Usage, in a component:
 *
 *   const { reactTranslate: t } = useTranslation();
 *   t('sitewide.save');                           // a plain string
 *   t('aiAllowance.barUsed', { percent: 62 });    // "{percent}% used" filled in
 *
 * Every literal key must exist in locales/en/common.json and, translated,
 * in locales/es/common.json: scripts/i18n-check.mjs fails otherwise. The
 * language itself is changed by saving the preference (UserConfigProvider),
 * not by i18n.changeLanguage, which changes it for this page load only.
 *
 * @module useTranslation
 */

import { useSyncExternalStore } from 'react';
import { t as translateFn, getLanguage, setLanguage, subscribeToLanguage } from '../i18n/i18nSetup';
import { Language } from '../types/i18n';

/**
 * Translation utility for React components.
 *
 * @returns {Object} Translation utilities
 * @property {Function} reactTranslate - Translation function that takes a translation key and returns translated text
 * @property {Object} i18n - i18n configuration object
 * @property {string} i18n.language - Current language code (e.g. 'en')
 * @property {Function} i18n.changeLanguage - Function to change the current language
 */
export function useTranslation(): {
  reactTranslate: (key: string, params?: Record<string, string | number>) => string;
  i18n: {
    language: Language;
    changeLanguage: (lang: Language) => void;
  };
} {
  const language = useSyncExternalStore(subscribeToLanguage, getLanguage, getLanguage);
  return {
    reactTranslate: translateFn,
    i18n: {
      language,
      changeLanguage: setLanguage
    }
  };
}